// Per-page contextual help. Keyed by route prefix — first matching key
// wins, so put more specific routes before generic ones.

export interface PageHelp {
  title: string;
  body: string;
}

const HELP: Array<[string, PageHelp]> = [
  [
    "/admin/tenants",
    {
      title: "Tenants — administration",
      body: `
Gere les tenants AlphaProx. Chaque ligne montre l'etat des 3 mappings
externes (NetBox / Keycloak / Vault).

**Re-provision** : idempotent, recree les mappings manquants. A utiliser
si NetBox/Keycloak/Vault etaient down lors du create initial.

**Trash** : refuse si VMs presentes (409). Confirmation cascade demandee
si tu insistes — les VMs locales sont supprimees mais pas sur Proxmox
(a faire avant pour eviter les orphelines).
      `.trim(),
    },
  ],
  [
    "/admin/users",
    {
      title: "Utilisateurs — administration",
      body: `
Cree/liste/supprime les utilisateurs Keycloak attaches a un tenant.

1. Selectionne un tenant dans le dropdown (le tenant doit avoir un
   keycloak_group_id, sinon 409 — re-provision via /admin/tenants).
2. Remplis le formulaire ; le mot de passe est temporary=true cote
   Keycloak (l'utilisateur le change a la 1ere connexion).
3. Tableau des membres : 🔑 reset password, jaune/vert toggle enabled,
   🗑️ delete.
      `.trim(),
    },
  ],
  [
    "/admin/audit",
    {
      title: "Audit log",
      body: `
Trace de toutes les mutations (tenant, user, vault, webhook NetBox).

**Filtres** : action_prefix (ex: \`vault.read\`, \`tenant.delete\`,
\`netbox.\`) et tenant_id.

Pills colorees : rouge=delete, vert=create, bleu=update.
Ligne cliquable → details JSON pretty-printed + user-agent.

Auto-refresh toutes les 30s.
      `.trim(),
    },
  ],
  [
    "/admin/vault",
    {
      title: "Vault — secrets per tenant",
      body: `
Explorer scope a \`tenants/<slug>/*\` — pas d'acces aux paths racine ni
a ceux des autres tenants.

Toute lecture ou ecriture est audit-loguee (action \`vault.read\`,
\`vault.write\`, \`vault.delete\`). Les valeurs ne sont jamais loguees,
seulement le chemin et les noms de cles.

**Reveler** : bouton dans le modal, ne persiste pas — re-cliquer pour
remasquer. Les valeurs ne sont pas mises en cache cote browser.
      `.trim(),
    },
  ],
  [
    "/onboarding",
    {
      title: "Quick start — onboarder un tenant",
      body: `
Wizard 3 etapes pour creer un tenant complet :

1. **Tenant** : nom + slug ; le backend auto-provisione NetBox tenant
   + Keycloak group + Vault path en parallele.
2. **Premiere IP** : choisis un prefix NetBox, une IP est allouee et
   liee au tenant.
3. **Resume** : tu vois les ids generes.

Si l'auto-provision NetBox/Keycloak echoue (service down), le tenant
est cree quand meme — relance via /admin/tenants Re-provision.
      `.trim(),
    },
  ],
  [
    "/knowledge",
    {
      title: "Knowledge — documents indexes",
      body: `
Upload de PDF/MD/TXT/DOCX/XLSX (max 50 MB). Indexation BGE-M3 cote
OMER core (proxy AlphaProx → OMER M3:6080).

Recherche semantique en langage naturel ; les resultats sont tries
par cosine similarity.

Les documents indexes sont visibles cross-tenant pour l'instant
(pas encore de scoping par tenant).
      `.trim(),
    },
  ],
  [
    "/provisioning",
    {
      title: "Provisioning — creer une VM",
      body: `
Le panneau bleu en haut liste ce qui sera applique automatiquement
(DNS, search domain, disclaimer MOTD).

**Tier A** : VM locale au site. **B** : HA same-site (replica pvesr).
**C** : DR cross-site (replica vers l'autre site).

Au succes, la page affiche les credentials initiaux + le rappel
cloud-init. La VM affichera le disclaimer AlphaProx a la 1ere
connexion SSH.
      `.trim(),
    },
  ],
  [
    "/ipam",
    {
      title: "IPAM — IP Address Management",
      body: `
Vue NetBox scopee au tenant courant : prefixes, IPs allouees, DNS
zones, DHCP leases.

**Sync VMs → NetBox** (en haut a droite) : retrofit les IPs des VMs
locales dans NetBox (utile apres une migration ou pour les VMs
provisioneees hors AlphaProx). Rate-limit 2 calls / 5 min.

Le bouton **Backfill (dry-run)** previsualise sans ecrire.
      `.trim(),
    },
  ],
  [
    "/topology",
    {
      title: "Topology",
      body: `
Graphe ReactFlow gauche-droite : sites → PVE nodes → VMs.

- Areas IPsec (orange anime) entre les 3 sites primaires.
- Areas mgmt (gris) bastion → tous les sites.
- Areas monitors (cyan) monitoring → tous les sites.

Filtres : tenant, tier, status, replicas, VLANs.
Click sur un node → panneau detail avec toutes les data.
      `.trim(),
    },
  ],
  [
    "/ops",
    {
      title: "Operations",
      body: `
Vue health globale :
- **OpsCards** : counts VMs / replicas / alerts / backups / LB.
- **Diagnostics** : status backup scheduler + scrape monitoring.
- **External integrations** : 4 probes live (Proxmox, NetBox, PBS,
  Keycloak). Rouge = action requise.

Si un point est rouge, regarde docs/user-guide/operator.md section
"Incidents frequents".
      `.trim(),
    },
  ],
];

const DEFAULT_HELP: PageHelp = {
  title: "Aide",
  body: `
Cette page n'a pas encore d'aide contextuelle dediee. Utilise le widget
chat en bas a droite pour poser une question — l'assistant a indexe
toute la documentation AlphaProx (architecture, user guides,
onboarding-trifecta).
  `.trim(),
};

export function getPageHelp(pathname: string): PageHelp {
  for (const [prefix, help] of HELP) {
    if (pathname.startsWith(prefix)) return help;
  }
  return DEFAULT_HELP;
}
